import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import ChooseDate from 'shell/components/shared/choice-date';
import ListGender from 'shell/components/shared/gender';
import { memo, useCallback } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import Foreign from 'shell/components/shared/consult-person/steps/registry-office/generality/foreign';
import Italian from 'shell/components/shared/consult-person/steps/registry-office/generality/italian';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import { FormikErrors, FormikTouched } from 'formik';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ForeignForm } from 'shell/view-models/foreign-form';
import { DateTime } from 'luxon';
import { DatePickerView } from '@mui/lab/DatePicker/shared';

interface FormGeneralityProps {
  isEditMode?: boolean;
  disabled?: boolean;
  generality?: AccordionGeneralityForm;
  person?: AccordionPersonForm;
  errorsGenerality?: FormikErrors<AccordionGeneralityForm>;
  touchedGenerality?: FormikTouched<AccordionGeneralityForm>;
  errorsPerson?: FormikErrors<AccordionPersonForm>;
  touchedPerson?: FormikTouched<AccordionPersonForm>;
  onChangePerson?: (
    key: keyof AccordionPersonForm,
    value: AccordionPersonForm[keyof AccordionPersonForm]
  ) => void;
  onChangeGenerality: (
    key: keyof AccordionGeneralityForm,
    value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
  ) => void;
  onChangeFieldsGenerality: (values: AccordionGeneralityForm) => void;
  onChangeFieldsPerson?: (values: AccordionPersonForm) => void;
  disabledBirthDate?: boolean;
  labelMunicipalityItalian?: string;
  labelProvinceItalian?: string;
  ForPracticeTemporary?: boolean;
  requiredFiscalCode?: boolean;
  disabledValidityFiscalCode?: boolean;
}

const FormGeneralityComponent = ({
  isEditMode,
  disabled,
  person,
  generality,
  errorsPerson,
  touchedPerson,
  errorsGenerality,
  touchedGenerality,
  onChangeGenerality,
  onChangePerson,
  onChangeFieldsGenerality,
  onChangeFieldsPerson,
  disabledBirthDate = false,
  labelMunicipalityItalian,
  labelProvinceItalian,
  requiredFiscalCode = false,
}: FormGeneralityProps): React.ReactElement => {
  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChangeGenerality('italian', {
        ...(generality || {}).italian,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChangeGenerality('foreign', {
        ...(generality || {}).foreign,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const handleListGenderChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChangeGenerality('gender', event.target.value);
  });

  const handleChooseDateChange = useEventCallback(
    (eventDate: DateTime | null, choice?: Array<DatePickerView>) => {
      onChangeFieldsGenerality({
        birthDate: eventDate,
        birthChoiceDate: choice,
      });
    }
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label={requiredFiscalCode ? 'Codice fiscale *' : 'Codice fiscale'}
          aria-label="Codice fiscale"
          value={person?.fiscalCode?.toUpperCase() || generality?.fiscalCode?.toUpperCase() || ''}
          onChange={(event) => {
            if (onChangePerson) {
              onChangePerson('fiscalCode', event.target.value);
            } else {
              onChangeGenerality('fiscalCode', event.target.value);
            }
          }}
          helperText={errorsPerson?.fiscalCode || errorsGenerality?.fiscalCode}
          error={
            (touchedPerson?.fiscalCode && Boolean(errorsPerson?.fiscalCode)) ||
            (touchedGenerality?.fiscalCode && Boolean(errorsGenerality?.fiscalCode))
          }
          fullWidth
          disabled={isEditMode ? false : disabled}
          inputProps={{ maxLength: '16' }}
        />
      </Grid>
      <Grid item container spacing={2} xs={12} md={10} alignItems="center">
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Cognome"
            aria-label="Cognome"
            id="surname"
            fullWidth
            disabled={
              isEditMode && (!person?.withoutSurname || !generality?.withoutSurname)
                ? false
                : disabled
            }
            helperText={errorsPerson?.surname || errorsGenerality?.surname}
            error={
              (touchedPerson?.surname && Boolean(errorsPerson?.surname)) ||
              (touchedGenerality?.surname && Boolean(errorsGenerality?.surname))
            }
            value={person?.surname || generality?.surname || ''}
            inputProps={{
              maxLength: '80',
            }}
            onChange={(event) => {
              if (onChangePerson) {
                onChangePerson('surname', event.target.value);
              } else {
                onChangeGenerality('surname', event.target.value);
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutSurname-replica"
                checked={
                  (person?.withoutSurname === undefined
                    ? generality?.withoutSurname
                    : person?.withoutSurname) || false
                }
                onChange={() => {
                  if (onChangeFieldsPerson) {
                    onChangeFieldsPerson({
                      withoutSurname: !person?.withoutSurname,
                      surname: !person?.withoutSurname ? '' : person?.surname,
                    });
                    return;
                  }
                  if (onChangeFieldsGenerality) {
                    onChangeFieldsGenerality({
                      withoutSurname: !generality?.withoutSurname,
                      surname: !generality?.withoutSurname ? '' : generality?.surname,
                    });
                  }
                }}
                inputProps={{ 'aria-label': 'Persona senza cognome' }}
                disabled={isEditMode ? false : disabled}
              />
            }
            label="Persona senza cognome"
          />
        </Grid>
      </Grid>
      <Grid item container spacing={2} xs={12} md={10} alignItems="center">
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Nome"
            aria-label="Nome"
            id="name"
            fullWidth
            disabled={
              isEditMode && (!person?.withoutName || !generality?.withoutName) ? false : disabled
            }
            inputProps={{
              maxLength: '80',
            }}
            helperText={errorsPerson?.name || errorsGenerality?.name}
            error={
              (touchedPerson?.name && Boolean(errorsPerson?.name)) ||
              (touchedGenerality?.name && Boolean(errorsGenerality?.name))
            }
            value={person?.name || generality?.name || ''}
            onChange={(event) => {
              if (onChangePerson) {
                onChangePerson('name', event.target.value);
              } else {
                onChangeGenerality('name', event.target.value);
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutName-replica"
                checked={
                  (person?.withoutName === undefined
                    ? generality?.withoutName
                    : person?.withoutName) || false
                }
                onChange={() => {
                  if (onChangeFieldsPerson) {
                    onChangeFieldsPerson({
                      withoutName: !person?.withoutName,
                      name: !person?.withoutName ? '' : person?.name,
                    });
                    return;
                  }
                  if (onChangeFieldsGenerality) {
                    onChangeFieldsGenerality({
                      withoutName: !generality?.withoutName,
                      name: !generality?.withoutName ? '' : generality?.name,
                    });
                  }
                }}
                inputProps={{ 'aria-label': 'Persona senza nome' }}
                disabled={isEditMode ? false : disabled}
              />
            }
            label="Persona senza nome"
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} md={4}>
          <ListGender
            value={generality?.gender || ''}
            onChange={handleListGenderChange}
            disabled={isEditMode ? false : disabled}
            error={Boolean(errorsGenerality?.gender) && touchedGenerality?.gender}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          {generality?.isForeign ? (
            <PmiTextField
              label="Anno Espatrio"
              id="expatriation-year"
              fullWidth
              disabled={isEditMode ? false : disabled}
              helperText={errorsGenerality?.expatriationYear}
              error={
                touchedGenerality?.expatriationYear && Boolean(errorsGenerality?.expatriationYear)
              }
              aria-label="Anno Espatrio"
              value={generality?.expatriationYear || ''}
              onChange={(event) => {
                onChangeGenerality(
                  'expatriationYear',
                  event.target.validity ? event.target.value : generality?.expatriationYear
                );
              }}
              inputProps={{ maxLength: '4' }}
            />
          ) : undefined}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} container spacing={2} alignItems="center">
        <ChooseDate
          selected={generality?.birthDate || null}
          onChange={handleChooseDateChange}
          choice={generality?.birthChoiceDate}
          disabled={isEditMode ? false : disabled || disabledBirthDate}
          error={touchedGenerality?.birthDate && Boolean(errorsGenerality?.birthDate)}
          helperText={errorsGenerality?.birthDate}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Nascita"
            id="isForeign"
            value={generality?.isForeign || 'false'}
            onChange={() => {
              if (onChangeFieldsGenerality) {
                onChangeFieldsGenerality({
                  isForeign: !generality?.isForeign,
                  expatriationYear: '',
                  italian: {
                    location: '',
                    province: '',
                    municipality: null,
                  },
                  foreign: {
                    state: null,
                    province: '',
                    municipality: null,
                  },
                });
                return;
              }
              onChangeGenerality('isForeign', !generality?.isForeign);
            }}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Nascita Italia"
              disabled={isEditMode ? false : disabled}
            />
            <FormControlLabel
              value="true"
              control={<Radio />}
              label="Nascita Estero"
              disabled={isEditMode ? false : disabled}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!generality?.isForeign ? (
        <Italian
          disabledComponent={isEditMode ? false : disabled}
          values={generality?.italian}
          onChange={onFieldItalianChange}
          errors={errorsGenerality?.italian as FormikErrors<ItalianForm>}
          touched={touchedGenerality?.italian as unknown as FormikTouched<ItalianForm>}
          labelMunicipality={labelMunicipalityItalian}
          labelProvince={labelProvinceItalian}
        />
      ) : (
        <Foreign
          disabledComponent={isEditMode ? false : disabled}
          values={generality?.foreign}
          onChange={onFieldForeignChange}
          errors={errorsGenerality?.foreign as FormikErrors<ForeignForm>}
          touched={touchedGenerality?.foreign as unknown as FormikTouched<ForeignForm>}
        />
      )}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          aria-label="Luogo di nascita eccezionale"
          label="Luogo di nascita eccezionale"
          id="exceptionalBirthplace"
          name="exceptionalBirthplace"
          fullWidth
          disabled={isEditMode ? false : disabled}
          type="text"
          variant="outlined"
          inputProps={{
            maxLength: '120',
          }}
          helperText={errorsGenerality?.exceptionalBirthPlace}
          error={
            touchedGenerality?.exceptionalBirthPlace &&
            Boolean(errorsGenerality?.exceptionalBirthPlace)
          }
          value={generality?.exceptionalBirthPlace || ''}
          onChange={(event) => {
            onChangeGenerality('exceptionalBirthPlace', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          aria-label="Identificativo del Comune"
          label="Identificativo del Comune"
          id="municipalityId"
          fullWidth
          disabled
          helperText={errorsGenerality?.municipalityIdentifier}
          error={
            touchedGenerality?.municipalityIdentifier &&
            Boolean(errorsGenerality?.municipalityIdentifier)
          }
          type="text"
          variant="outlined"
          value={generality?.municipalityIdentifier || ''}
          onChange={(event) => {
            onChangeGenerality('municipalityIdentifier', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          aria-label="Codice Istat"
          label="Codice Istat"
          id="istatCode"
          fullWidth
          disabled
          type="text"
          variant="outlined"
          value={generality?.istatCode || ''}
          onChange={(event) => {
            onChangeGenerality('istatCode', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          aria-label="Identificativo ANPR"
          label="Identificativo ANPR"
          id="anprId"
          name="anprId"
          fullWidth
          disabled
          type="text"
          variant="outlined"
          value={generality?.anprIdentifier || ''}
          onChange={(event) => {
            onChangeGenerality('anprIdentifier', event.target.value);
          }}
        />
      </Grid>
    </Grid>
  );
};

FormGeneralityComponent.displayName = 'Form generality component';

const FormGenerality = memo(FormGeneralityComponent);

export default FormGenerality;
